/** Empfehlungen — sechs Wege zu einem neuen Buch.
 *
 *  katalog   aus dem ganzen Regal
 *  tags      nach einzelnen Schlagworten
 *  mixer     Wölkchen: Tags in „in diese Richtung" oder „bloß nicht" ziehen
 *  nebenan   aus dem Regal der anderen Person (der größte Fundus, weil sich
 *            die beiden Bibliotheken kaum überschneiden)
 *  duett     ein Buch für beide
 *  blinddate verhülltes Buch, nur Stimmung und ein Satz
 *
 *  Ohne KI-Schlüssel liefern alle Modi Vorschläge aus den vorhandenen Regalen
 *  statt neuer Titel. Das steht dann auch so da — nichts wird vorgetäuscht.
 */

import { useEffect, useState } from "react";
import type { Recommendation, RecoMode } from "../../../shared/types";
import { OWNER_NAME, RECO_LABEL, other } from "../../../shared/types";
import * as api from "../lib/api";
import { useStore } from "../lib/store";

const MODI: RecoMode[] = [
  "katalog",
  "nebenan",
  "mixer",
  "tags",
  "duett",
  "blinddate",
];

function Wolke({
  tags,
  ja,
  nein,
  setzen,
}: {
  tags: { name: string; count: number }[];
  ja: string[];
  nein: string[];
  setzen: (ja: string[], nein: string[]) => void;
}) {
  // Ein Klick schaltet weiter: neutral → in diese Richtung → bloß nicht → neutral.
  // Bewusst kein Ziehen mit der Maus: Touch soll gleichwertig sein.
  function weiter(name: string) {
    if (ja.includes(name))
      setzen(
        ja.filter((t) => t !== name),
        [...nein, name],
      );
    else if (nein.includes(name))
      setzen(
        ja,
        nein.filter((t) => t !== name),
      );
    else setzen([...ja, name], nein);
  }

  return (
    <div className="karte">
      <span className="feldname">
        Wölkchen — einmal tippen: in diese Richtung · zweimal: bloß nicht ·
        dreimal: egal
      </span>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7 }}>
        {tags.map((t) => (
          <button
            key={t.name}
            className={`chip${ja.includes(t.name) ? " an" : nein.includes(t.name) ? " nein" : ""}`}
            onClick={() => weiter(t.name)}
          >
            {ja.includes(t.name) ? "＋ " : nein.includes(t.name) ? "− " : ""}
            {t.name} <span style={{ opacity: 0.5 }}>{t.count}</span>
          </button>
        ))}
        {!tags.length && (
          <span className="lauftext" style={{ margin: 0, fontSize: 14 }}>
            Noch keine Schlagworte im Regal — erst in der Werkstatt
            verschlagworten.
          </span>
        )}
      </div>
      {(ja.length > 0 || nein.length > 0) && (
        <p className="lauftext" style={{ fontSize: 14, marginTop: 12 }}>
          {ja.length > 0 && (
            <>
              In diese Richtung: <strong>{ja.join(", ")}</strong>.{" "}
            </>
          )}
          {nein.length > 0 && (
            <>
              Bloß nicht: <strong>{nein.join(", ")}</strong>.
            </>
          )}
        </p>
      )}
    </div>
  );
}

function Vorschlag({
  reco,
  onUrteil,
}: {
  reco: Recommendation;
  onUrteil: (id: number, body: { hide?: boolean; save?: boolean }) => void;
}) {
  const [gelueftet, setGelueftet] = useState(reco.mode !== "blinddate");
  const bild = reco.isbn ? api.coverUrl(reco.isbn, "s") : null;

  return (
    <article
      className="karte"
      style={{ display: "flex", gap: 18, flexWrap: "wrap" }}
    >
      {gelueftet && bild && (
        <img
          src={bild}
          alt=""
          style={{ width: 88, height: "auto", boxShadow: "var(--sh-near)" }}
          loading="lazy"
        />
      )}
      <div style={{ flex: "1 1 300px" }}>
        {gelueftet ? (
          <>
            <h3
              style={{
                fontFamily: "var(--display)",
                fontWeight: 400,
                fontSize: 20,
                margin: "0 0 2px",
              }}
            >
              {reco.title}
            </h3>
            <p className="klein" style={{ marginBottom: 12 }}>
              {reco.author}
              {/* Drei Herkünfte, die auseinandergehalten werden müssen: ein Buch
                  aus einem der Regale, ein einmalig vorbereiteter Vorschlag von
                  außerhalb, oder ein frisch von der KI erzeugter. Ein Vorrats-
                  Treffer darf nicht als KI-Vorschlag erscheinen — daran war
                  keine KI beteiligt. */}
              {reco.source === "lokal"
                ? " · aus einem der beiden Regale"
                : reco.source === "vorrat"
                  ? " · neu — aus dem Vorrat"
                  : " · Vorschlag der KI"}
            </p>
          </>
        ) : (
          <>
            <h3
              style={{
                fontFamily: "var(--display)",
                fontWeight: 400,
                fontSize: 20,
                margin: "0 0 8px",
              }}
            >
              Verhülltes Buch
            </h3>
            <p className="lauftext" style={{ fontSize: 15 }}>
              {reco.teaser ||
                "Kein Hinweis vorhanden — dieses Buch bleibt eine Überraschung."}
            </p>
          </>
        )}

        {gelueftet && reco.why && (
          <p className="lauftext" style={{ fontSize: 15, margin: "0 0 10px" }}>
            {reco.why}
          </p>
        )}

        {gelueftet && reco.anchors?.length > 0 && (
          <p
            className="klein"
            style={{
              marginBottom: 10,
              letterSpacing: "0.12em",
              textTransform: "none",
            }}
          >
            weil dir gefallen hat:{" "}
            {reco.anchors.map((a) => a.title).join(" · ")}
          </p>
        )}

        {gelueftet && reco.tags?.length > 0 && (
          <div
            style={{
              display: "flex",
              gap: 6,
              flexWrap: "wrap",
              marginBottom: 12,
            }}
          >
            {reco.tags.map((t) => (
              <span key={t} className="chip">
                {t}
              </span>
            ))}
          </div>
        )}

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {!gelueftet && (
            <button className="knopf stark" onClick={() => setGelueftet(true)}>
              Herausziehen
            </button>
          )}
          <button
            className="knopf"
            onClick={() => onUrteil(reco.id, { save: !reco.saved_at })}
          >
            {reco.saved_at ? "gemerkt ✓" : "merken"}
          </button>
          <button
            className="knopf"
            onClick={() => onUrteil(reco.id, { hide: true })}
          >
            weglegen
          </button>
        </div>
      </div>
    </article>
  );
}

export function Empfehlungen() {
  const { owner, modus, ki, melde } = useStore();
  const [mode, setMode] = useState<RecoMode>("nebenan");
  const [tags, setTags] = useState<{ name: string; count: number }[]>([]);
  const [ja, setJa] = useState<string[]>([]);
  const [nein, setNein] = useState<string[]>([]);
  const [liste, setListe] = useState<Recommendation[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  const [quelle, setQuelle] = useState<"ki" | "lokal" | null>(null);

  useEffect(() => {
    if (modus !== "server") return;
    api
      .tags(owner)
      .then((res) =>
        setTags(res.data.cloud.map((t) => ({ name: t.name, count: t.count }))),
      )
      .catch(() => setTags([]));
    api
      .recommendations(owner)
      .then((res) => setListe(res.data.filter((r) => r.mode === mode)))
      .catch(() => setListe([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [owner, modus, mode]);

  async function erzeugen() {
    if (modus !== "server") {
      melde("Empfehlungen brauchen den Server.", "fehler");
      return;
    }
    setLaeuft(true);
    try {
      const res = await api.recommend(owner, {
        mode,
        wanted: mode === "mixer" || mode === "tags" ? ja : undefined,
        unwanted: mode === "mixer" ? nein : undefined,
      });
      setListe(res.data.vorschlaege);
      setQuelle(res.data.quelle);
      if (res.message)
        melde(res.message, res.status === "warnung" ? "warnung" : "ok");
    } catch (err) {
      melde(err instanceof Error ? err.message : String(err), "fehler");
    } finally {
      setLaeuft(false);
    }
  }

  async function urteilen(
    id: number,
    body: { hide?: boolean; save?: boolean },
  ) {
    try {
      await api.judgeRecommendation(owner, id, body);
      setListe((alle) =>
        body.hide
          ? alle.filter((r) => r.id !== id)
          : alle.map((r) =>
              r.id === id ? { ...r, saved_at: body.save ? "jetzt" : null } : r,
            ),
      );
    } catch (err) {
      melde(err instanceof Error ? err.message : String(err), "fehler");
    }
  }

  return (
    <>
      <section style={{ maxWidth: "48ch" }}>
        <h2 className="titelzeile">Was als Nächstes?</h2>
        <p className="lauftext">
          Sechs Wege zu einem Vorschlag. Jeder nennt Bücher aus{" "}
          {OWNER_NAME[owner]}s Regal als Begründung — so ist sofort erkennbar,
          ob der Vorschlag etwas taugt.
        </p>
        {!ki && (
          <p
            className="lauftext"
            style={{ fontSize: 14, color: "var(--warn)" }}
          >
            Kein KI-Schlüssel hinterlegt: Die Vorschläge kommen aus den beiden
            Regalen und aus dem einmalig vorbereiteten Vorrat. Nur für frisch
            erdachte Titel braucht es den Schlüssel als Worker-Secret.
          </p>
        )}
      </section>

      <div
        style={{
          display: "flex",
          gap: 8,
          flexWrap: "wrap",
          margin: "28px 0 18px",
        }}
      >
        {MODI.map((m) => (
          <button
            key={m}
            className={`chip${mode === m ? " an" : ""}`}
            onClick={() => setMode(m)}
          >
            {RECO_LABEL[m]}
          </button>
        ))}
      </div>

      <p className="lauftext" style={{ fontSize: 14 }}>
        {mode === "nebenan" &&
          `Bücher aus ${OWNER_NAME[other(owner)]}s Regal, die zu ${OWNER_NAME[owner]} passen könnten.`}
        {mode === "duett" &&
          "Ein Buch, das beiden gefallen könnte — die schwierigste Frage von allen."}
        {mode === "blinddate" &&
          "Nur Stimmung und ein Satz. Der Titel zeigt sich erst beim Herausziehen."}
        {mode === "katalog" && "Aus dem gesamten Regal abgeleitet."}
        {mode === "tags" && "Erst Schlagworte wählen, dann vorschlagen lassen."}
        {mode === "mixer" &&
          "Tags in die gewünschte Richtung schieben — und was gar nicht geht, ausschließen."}
      </p>

      {(mode === "mixer" || mode === "tags") && (
        <div style={{ marginTop: 16 }}>
          <Wolke
            tags={tags}
            ja={ja}
            nein={mode === "mixer" ? nein : []}
            setzen={(j, n) => {
              setJa(j);
              setNein(mode === "mixer" ? n : []);
            }}
          />
        </div>
      )}

      <div
        style={{
          display: "flex",
          gap: 12,
          alignItems: "center",
          marginTop: 20,
          flexWrap: "wrap",
        }}
      >
        <button
          className="knopf stark"
          disabled={laeuft}
          onClick={() => void erzeugen()}
        >
          {laeuft ? "sucht …" : "Vorschläge holen"}
        </button>
        {quelle && (
          <span className="klein">
            Quelle: {quelle === "ki" ? "KI" : "vorhandene Regale"}
          </span>
        )}
      </div>

      <div style={{ display: "grid", gap: 14, marginTop: 30 }}>
        {liste.map((r) => (
          <Vorschlag key={r.id} reco={r} onUrteil={urteilen} />
        ))}
      </div>

      {!laeuft && !liste.length && (
        <p className="lauftext" style={{ marginTop: 26 }}>
          Noch keine Vorschläge in dieser Richtung.
        </p>
      )}
    </>
  );
}
